<template>
  <div class="scroll__scroll-wrapper">
    <button v-show="canScrollPrev" class="scroll__arrow scroll__arrow--left" type="button" aria-label="prev"
      @click="scrollPrev">
      <ChevronLeft />
    </button>
    <div ref="scrollRef" class="scroll__track" :class="{ 'scroll__track--even': !noEvenMode && isEvenMode }"
      @scroll="onScroll">
      <slot />
    </div>
    <button v-show="canScrollNext" class="scroll__arrow scroll__arrow--right" type="button" aria-label="next"
      @click="scrollNext">
      <ChevronRight />
    </button>
  </div>
</template>

<script setup lang="ts">
import ChevronLeft from '@/assets/icons/chevron-left.svg'
import ChevronRight from '@/assets/icons/chevron-right.svg'

const scrollRef = ref<HTMLDivElement | null>(null)
const canScrollNext = ref(false)
const canScrollPrev = ref(false)
const childCount = ref(0)
const isEvenMode = ref(false)
const isLoopMode = ref(false)

let loopStartOffset = 0
let loopSetWidth = 0

defineProps<{
  noEvenMode?: boolean
}>();

function countChildren(): void {
  const el = scrollRef.value
  if (!el) return
  const children = Array.from(el.children).filter(
    (child) => child.nodeType === Node.ELEMENT_NODE && !(child as HTMLElement).dataset.loopClone
  )
  const newCount = children.length
  if (newCount !== childCount.value) {
    childCount.value = newCount
    updateLayout()
  }
  updateScrollState()
}

function updateLayout(): void {
  const el = scrollRef.value
  if (!el) return
  const isMobile = window.innerWidth < 768

  if (isLoopMode.value) {
    teardownLoop()
    isLoopMode.value = false
  }

  if (isMobile) {
    isEvenMode.value = false
    el.style.overflowX = 'hidden'
  } else {
    if (childCount.value > 0 && childCount.value < 5) {
      isEvenMode.value = true
      el.style.overflowX = 'hidden'
    } else {
      isEvenMode.value = false
      el.style.overflowX = 'auto'

    }
    if (el.scrollWidth > el.clientWidth + 4) {
      isLoopMode.value = true
      setupLoop()
    }
  }
  updateScrollState()
}

function updateScrollState(): void {
  const el = scrollRef.value
  if (!el) return

  if (isLoopMode.value) {
    canScrollNext.value = true
    canScrollPrev.value = true
  } else {
    canScrollNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    canScrollPrev.value = el.scrollLeft > 10
  }
}

function onScroll(): void {
  if (isLoopMode.value) {
    handleLoopScroll()
  }
  updateScrollState()
}

function handleLoopScroll(): void {
  const el = scrollRef.value
  if (!el || loopSetWidth === 0) return

  if (el.scrollLeft < loopStartOffset) {
    el.scrollLeft += loopSetWidth
  } else if (el.scrollLeft >= loopStartOffset + loopSetWidth) {
    el.scrollLeft -= loopSetWidth
  }
}

function setupLoop(): void {
  const el = scrollRef.value
  if (!el) return

  const children = Array.from(el.children).filter(
    (child) => child.nodeType === Node.ELEMENT_NODE
  ) as HTMLElement[]
  if (children.length === 0) return

  for (let i = children.length - 1; i >= 0; i--) {
    const clone = children[i].cloneNode(true) as HTMLElement
    clone.setAttribute('aria-hidden', 'true')
    clone.dataset.loopClone = 'true'
    clone.style.pointerEvents = 'none'
    el.insertBefore(clone, el.firstChild)
  }

  const rightClones = children.map((child) => {
    const clone = child.cloneNode(true) as HTMLElement
    clone.setAttribute('aria-hidden', 'true')
    clone.dataset.loopClone = 'true'
    clone.style.pointerEvents = 'none'
    el.appendChild(clone)
    return clone
  })

  const firstOriginal = children[0]
  const firstRightClone = rightClones[0]
  if (firstOriginal && firstRightClone) {
    loopStartOffset = firstOriginal.offsetLeft
    loopSetWidth = firstRightClone.offsetLeft - firstOriginal.offsetLeft
    el.scrollLeft = loopStartOffset
  }
}

function teardownLoop(): void {
  const el = scrollRef.value
  if (!el) return
  el.querySelectorAll('[data-loop-clone]').forEach((clone) => clone.remove())
  loopStartOffset = 0
  loopSetWidth = 0
  el.scrollLeft = 0
}

function scrollNext(): void {
  const el = scrollRef.value
  if (!el) return
  const firstChild = el.firstElementChild as HTMLElement | null
  const cardWidth = firstChild?.offsetWidth || 280
  const gap = 24
  el.scrollBy({ left: cardWidth + gap, behavior: 'smooth' })
}

function scrollPrev(): void {
  const el = scrollRef.value
  if (!el) return
  const firstChild = el.firstElementChild as HTMLElement | null
  const cardWidth = firstChild?.offsetWidth || 280
  const gap = 24
  el.scrollBy({ left: -(cardWidth + gap), behavior: 'smooth' })
}

let resizeObserver: ResizeObserver | null = null
let mutationObserver: MutationObserver | null = null

onMounted(() => {
  if (scrollRef.value) {
    countChildren()

    resizeObserver = new ResizeObserver(() => {
      updateLayout()
      updateScrollState()
    })
    resizeObserver.observe(scrollRef.value)

    mutationObserver = new MutationObserver(() => {
      countChildren()
    })
    mutationObserver.observe(scrollRef.value, { childList: true, subtree: false })
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  mutationObserver?.disconnect()
  teardownLoop()
})
</script>

<style lang="scss" scoped>
.scroll__scroll-wrapper {
  position: relative;
}

.scroll__track {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0.25rem 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 0.5rem;
  align-items: center;
  overflow-x: hidden;
  justify-content: center;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: $breakpoint-md) {
    flex-direction: row;
    margin: 0 auto;
    gap: 1.5rem;
    align-items: center;
    padding: 0.5rem 0;

    &.scroll__track--even {
      width: 100%;

      >* {
        flex: 1;
        max-width: 100%;
        min-height: stretch;
      }
    }

    &:not(.scroll__track--even) {
      overflow-x: auto;
      overflow-y: visible;
      justify-content: start;

      >* {
        flex-shrink: 0;
      }
    }
  }
}

.scroll__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  backdrop-filter: blur(8px);
  transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);

  @media (min-width: $breakpoint-md) {
    width: 56px;
    height: 56px;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.5);
    transform: translateY(-50%) scale(1.08);
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }

  &--left {
    left: -0.5rem;

    @media (min-width: $breakpoint-md) {
      left: -1rem;
    }
  }

  &--right {
    right: -0.5rem;

    @media (min-width: $breakpoint-md) {
      right: -1rem;
    }
  }
}

@media (max-width: $breakpoint-md) {
  .scroll__arrow {
    display: none;
  }
}
</style>
